import { MeterRole } from './device.model';

export interface PartnerModel {
  partnerId: string;
  partnerCity: string;
  partnerName: string;
  partnerPostalcode: string;
  partnerVat: string;
  partnerChamberOfCommerceNo: string;
  partnerCountry: string;
  partnerOid: string;
  partnerStatus: string;
  partnerType: string;
  partnerImage: string;
  /** Partner administrator's email; only returned to / shown for Xemex users. */
  partnerEmail?: string;
  // Absent until the backend read-response is extended; consumers should treat it as
  // unset (no default assumed) in the meantime. Field name matches the backend's Partner
  // response model 1:1 (partnerXxx-prefixed), same as every other field here.
  partnerDefaultMeterRole?: MeterRole;
  // Minutes between two DLMS readouts, managed by Xemex and not editable from the portal.
  // Absent until the backend read-response is extended; consumers fall back to the previous
  // fixed cadence rather than rendering an empty value.
  partnerReadoutFrequencyMinutes?: number;
}

const READOUT_FREQUENCY_PREFIX = 'companies.detailTabs.general.dataReadout.';

/** Cadences that have a name of their own. Anything else is rendered as a plain minute count. */
const NAMED_READOUT_FREQUENCIES: Record<number, string> = {
  1440: 'frequencyOncePerDay',
  720: 'frequencyTwicePerDay',
  360: 'frequencyFourTimesPerDay',
  60: 'frequencyHourly',
};

const READOUT_FREQUENCY_FALLBACK = 'frequencyTwicePerDay';

export interface ReadoutFrequency {
  key: string;
  params: Record<string, number>;
}

/** The readout cadence as a translation key plus its interpolation parameters. A partner whose
 *  frequency the backend does not return yet keeps the cadence the card showed before the field
 *  existed, so the value never reads as an error or an empty field. Shared between the my-company
 *  and partner-detail general tabs — both render the same backend field the same way. */
export function getReadoutFrequency(partnerModel: PartnerModel | undefined): ReadoutFrequency {
  const minutes = partnerModel?.partnerReadoutFrequencyMinutes;

  if (minutes === undefined || minutes === null) {
    return { key: READOUT_FREQUENCY_PREFIX + READOUT_FREQUENCY_FALLBACK, params: {} };
  }

  const named = NAMED_READOUT_FREQUENCIES[minutes];
  if (named) {
    return { key: READOUT_FREQUENCY_PREFIX + named, params: {} };
  }

  return { key: READOUT_FREQUENCY_PREFIX + 'frequencyEveryNMinutes', params: { minutes } };
}

export interface PartnerResponseModel {
  partners: PartnerModel[];
  numberOfItems: number;
}

export interface CountryModel {
  id: string;
  name: string;
}

export interface CompanyPayloadModel {
  CompanyType?: string;
  city?: string;
  country?: string;
  status?: string;
  parentCompany?: string;
  companySizes?: string;
  partnerOid?: string;
  locationId?: string;
  partnerName?: string;
}

export interface CompanyFiltersModel extends CompanyPayloadModel {
  limit: number;
  offset: number;
}

export enum CompanyDetailTab {
  General,
  Users,
  Installers,
  Locations,
  Devices,
  WhiteLabel,
  Services,
}

export enum CompanyTypeEnum {
  Customer = 'customer',
  Installer = 'installer',
  SubInstaller = 'subInstaller',
}

export enum CompanyStatusEnum {
  Active = 'active',
  Inactive = 'inactive',
  PendingVerification = 'pendingVerification',
}

export interface WhiteLabelUpdateModel {
  primary_color?: string;
  secondary_color?: string;
  open_graph_title?: string;
  default_logo_uri?: any;
  favicon_logo_uri?: any;
  small_logo_uri?: any;
}
